<template>
  <div :class="['aiaibot-dropdown', { opened }]" ref="container">
    <template v-if="!clicked">
      <h4 class="aiaibot-dropdown--heading">{{ heading }}:</h4>
      <button
        class="aiaibot-dropdown--button aiaibot-content tw-p-3 tw-rounded-t-lg tw-rounded-l-lg tw-bg-green-light-25"
        :disabled="disabled"
        @click="toggleList"
      >

        {{ selectedItem || t('aiaibot_dropdown_choose') }}
        <Icon
          class="aiaibot-dropdown--button-chevron"
          art="regular"
          :height="13"
          name="chevron-right"
          color="black"
        />
      </button>
      <div
        class="aiaibot-dropdown--list tw-rounded-lg"
        :style="{ minWidth: `${listMinWidth}px` }"
      >
        <button
          @click="toggleList"
          :class="['aiaibot-dropdown--list-item label tw-p-3', {
            selected: !selectedItem
          }]"
        >
          <Icon
            art="regular"
            :height="13"
            name="check"
            color="gray"
            class="icon"
          />
          {{ t('aiaibot_dropdown_choose') }}
        </button>
        <div class="aiaibot-dropdown--list-scroll">
          <button
            v-for="option in options"
            :key="option"
            @click="selectItem(option)"
            :class="['aiaibot-dropdown--list-item tw-p-3', {
              selected: option === selectedItem
            }]"
          >
            <Icon
              art="regular"
              :height="13"
              name="check"
              color="white"
              class="icon"
            />
            {{ option }}
          </button>
        </div>
      </div>
      <ChatButton
        v-if="selectedItem"
        @click="onConfirm"
        :label="t('aiaibot_option_list_confirm', { option: selectedItem })"
        :types="['primary']"
        class="aiaibot-dropdown--confirm"
      />
    </template>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { registerOutsideClick, disposeOutsideClick } from '@/helpers/clickOutside'
import store from '@/store'
import ChatButton from '@/components/ChatButton'
import Icon from '@/components/Icon'

const OUTSIDE_CLICK_ID = 'AiaiBotOptionList'

export default {
  name: 'AiaiBotOptionList',
  components: { ChatButton, Icon },
  props: {
    options: {
      type: Array,
      default: () => []
    },
    heading: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const { $label: t } = useI36n()

    const clicked = ref(false)
    const container = ref(null)
    const listMinWidth = ref(0)
    const opened = ref(false)
    const selectedItem = ref('')

    const disabled = computed(() =>
      store.state.conversation.conversation.status === 'AIAIBOT_ENDED'
    )

    const getMessagePayload = optionValue => ({
      msgType: 'AIAIBOT_FEEDBACK',
      widgetType: 'button',
      content: optionValue
    })

    const onConfirm = () => {
      store.dispatch(
        'conversation/sendMessage',
        getMessagePayload(selectedItem.value)
      )
      clicked.value = true
    }

    const scrollToEnd = () => {
      setTimeout(() => { emit('scrollToEnd') }, 100)
    }

    const selectItem = value => {
      selectedItem.value = value
      toggleList()
    }

    const toggleList = () => {
      if (!opened.value) {
        registerOutsideClick(OUTSIDE_CLICK_ID, e => {
          if (!e.target.closest('.aiaibot-dropdown')) {
            opened.value = false
          }
        })

        if (container.value) {
          listMinWidth.value = container.value.offsetWidth
        }
      } else {
        disposeOutsideClick(OUTSIDE_CLICK_ID)
      }

      opened.value = !opened.value
      scrollToEnd()
    }

    return {
      clicked,
      container,
      disabled,
      listMinWidth,
      opened,
      selectedItem,
      t,
      onConfirm,
      selectItem,
      toggleList,
    }
  }
}
</script>

<style lang="postcss" scoped>
.aiaibot-dropdown {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.aiaibot-dropdown--heading {
  margin-bottom: 5px;
  font-size: 90%;
  font-weight: 400;
}

.aiaibot-dropdown--button,
.aiaibot-dropdown--list-item {
  font-size: 100%;
  color: var(--tw-c-dark-gray);
}

.aiaibot-dropdown--button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--tw-c-original-green);
  cursor: pointer;
  z-index: 0;

  &:disabled {
    color: #aaa;
    cursor: default;
    fill: white;
    background-color: #e6e6e6;
    border-color: #e0e0e0;

    .aiaibot-dropdown--button-chevron {
      opacity: 0.25;
    }
  }
}

.aiaibot-dropdown--button-chevron {
  transform: rotate(90deg);
}

.aiaibot-dropdown--list {
  position: absolute;
  bottom: 10px;
  top: 75px;
  right: 20px;
  display: none;
  flex-direction: column;
  justify-content: flex-start;
  margin: -63px -10px 0 10px;
  border: 1px solid var(--tw-c-original-green);
  background: var(--tw-c-original-green-lighten-12);
  box-shadow: 0 0 5px rgba(0,0,0, 0.15);
  overflow: hidden;
  z-index: 1;

  .opened & {
    display: flex;
  }
}

.aiaibot-dropdown--list-scroll {
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.aiaibot-dropdown--list-item {
  padding-left: 10px;
  padding-left: 10px;
  text-align: left;
  border: none;
  border-bottom: 1px solid var(--tw-c-original-green-lighten-25);
  border-radius: 0;
  background: var(--tw-c-original-green-lighten-12);

  &:last-child {
    border-bottom: 0;
  }

  &.label {
    color: var(--tw-c-cool-gray);
  }

  &:not(.selected) {
    padding-left: 30px;

    .icon {
      display: none;
    }
  }

  &:not(.label) {
    &:hover,
    &.selected {
      color: white;
      background: var(--tw-c-sanitas-green);
      border-color: var(--tw-c-sanitas-green);
      border-bottom-color: var(--tw-c-original-green-lighten-25);
      cursor: pointer;
    }
  }
}

.aiaibot-dropdown--confirm {
  margin-top: 10px;
}
</style>
